import React, { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { kbControlsLayerDefault, useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../../molecules/KeyboardControls'
import { HighlightEffect } from '../../../enums/KeyboardControls'

export const idOverlayModalCloseButton = 'modal-close-button'
export const OVERLAY_MODAL_LAYER_CLASS = 'fixed inset-0 bg-black bg-opacity-75 z-50'

interface OverlayModalProps {
  onClick: () => void
  children: React.ReactNode
  overlayWidth?: string
  overlayHeight?: string
  showOverlay?: boolean
  kbControlsLayer?: string
  innerBgClassName?: string
  isScrollable?: boolean
}

const OverlayMenuModal: React.FunctionComponent<OverlayModalProps> = ({
  onClick,
  children,
  overlayWidth = '90vw',
  overlayHeight = '85vh',
  showOverlay = true,
  kbControlsLayer = kbControlsLayerDefault,
  innerBgClassName = 'bg-black',
  isScrollable = true
}) => {
  const keyboardControls = useKeyboardControls()
  const baseContainerClasses = 'flex justify-center items-center'
  const overlayClasses = showOverlay ? OVERLAY_MODAL_LAYER_CLASS : ''

  const modalStyles: CSSProperties = showOverlay
    ? {
        width: overlayWidth,
        height: overlayHeight,
        maxWidth: 'calc(100vw - 40px)',
        maxHeight: 'calc(100vh - 40px)',
        minWidth: '670px',
        minHeight: '400px'
      }
    : {
        minWidth: '670px',
        minHeight: '400px'
      }

  const overlay = (
    <div
      data-testid='overlay-container'
      className={`${baseContainerClasses} ${overlayClasses}`}
    >
      <div className='glow-effect'>
        <div className='modal-container'>
          <div
            data-testid='modal-container'
            className='beveled-corner p-[4px] bg-dopage-lightGreen'
            style={modalStyles}
          >
            <div className={`inner ${innerBgClassName} h-full`}>
              <div className='p-[32px] flex flex-col items-center text-dopage-lightGreen h-full'>
                <div className={`w-full h-full ${isScrollable ? 'overflow-y-auto' : ''}`}>
                  {children}
                </div>
              </div>
            </div>
          </div>
          {showOverlay && (
            <div className='close-button-container absolute top-0 right-0'>
              <ControlHighlight
                active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idOverlayModalCloseButton)}
                highlightEffect={HighlightEffect.ButtonHexOutline}
                className='close-highlight'
              >
                <KeyboardControl layer={kbControlsLayer}>
                  <button
                    id={idOverlayModalCloseButton}
                    onClick={onClick}
                    aria-label='Close'
                    tabIndex={-1}
                    className='modal-close'
                  >
                    <span className='close-symbol' aria-hidden='true'>
                      ×
                    </span>
                  </button>
                </KeyboardControl>
              </ControlHighlight>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // Full-screen overlays must leave parent stacking contexts (dashboard header
  // is z-10; level tiles are also z-10 and paint later). Portal to body.
  if (showOverlay) {
    return createPortal(overlay, document.body)
  }

  return overlay
}

export default OverlayMenuModal
